/**
 * One row per equipment slot: what to wear, what to enchant it with, where it comes from, and
 * the other items the agent offered for that slot.
 *
 * A slot the agent left empty is still shown, so the table reads as a complete character sheet
 * rather than a list that silently skips what it could not fill. The build's owner gets a select
 * per slot to wear another of the agent's options; everyone else sees those options as links.
 */
import { BUILD_SLOTS, BUILD_SLOT_LABELS, type BuildGearRow, type BuildSlot, type ItemSourceView } from '@thottbot/shared';
import { ArtifactLink } from '../../components/ArtifactLink';
import { Badge } from '../../components/Badge';
import { gearChoices, selectionFor } from './gearChoices';

/** The first couple of sources, each labelled with where it is when that is known. */
function Sources({ sources, unknown }: { sources: ItemSourceView[]; unknown: boolean }) {
  if (unknown || sources.length === 0) return <span className="muted">source unknown</span>;
  return (
    <>
      {sources.slice(0, 2).map((s, i) => (
        <span key={`${s.kind}-${s.slug ?? s.name}-${i}`}>
          {i > 0 && ', '}
          {s.slug && s.type ? <ArtifactLink type={s.type} slug={s.slug} name={s.name} /> : s.name}
          {s.kind === 'dungeon' || s.kind === 'raid' ? <span className="muted"> ({s.kind})</span> : null}
        </span>
      ))}
    </>
  );
}

export interface GearTableProps {
  gear: BuildGearRow[];
  /** The owner of a ready build may change each slot's option. */
  editable?: boolean;
  /** `null` goes back to the agent's pick. */
  onSelect?: (slot: BuildSlot, artifactId: string | null) => void;
  /** The last save that failed, shown on its row. */
  error?: { slot: BuildSlot; message: string } | null;
}

function OptionsCell({ row, gear, editable, onSelect }: { row: BuildGearRow; gear: BuildGearRow[]; editable: boolean; onSelect?: GearTableProps['onSelect'] }) {
  const chosen = row.options.find((o) => o.item.artifactId === row.selected);
  const others = row.options.filter((o) => o.item.artifactId !== row.selected);
  const reason = chosen && !chosen.recommended && chosen.reason ? <div className="muted">{chosen.reason}</div> : null;

  if (editable && row.options.length > 1) {
    return (
      <>
        <select
          aria-label={`${BUILD_SLOT_LABELS[row.slot]} options`}
          value={row.selected}
          disabled={!!row.blockedBy}
          onChange={(e) => onSelect?.(row.slot, selectionFor(row, e.target.value))}
        >
          {gearChoices(row, gear).map((c) => (
            <option key={c.value} value={c.value} disabled={c.disabled}>
              {c.label}
            </option>
          ))}
        </select>
        {reason}
      </>
    );
  }

  if (others.length === 0) return reason ?? <span className="muted">&mdash;</span>;
  return (
    <>
      {reason}
      <div className="muted">Other options:</div>
      <ul className="gear-options">
        {others.map((o) => (
          <li key={o.item.artifactId}>
            <ArtifactLink type="item" slug={o.item.slug} name={o.item.name} quality={o.item.quality} />
            {o.reason && <span className="muted"> &ndash; {o.reason}</span>}
          </li>
        ))}
      </ul>
    </>
  );
}

export function GearTable({ gear, editable = false, onSelect, error }: GearTableProps) {
  const bySlot = new Map(gear.map((g) => [g.slot, g]));
  return (
    <div className="table-scroll">
      <table className="grid" data-testid="build-gear">
        <thead>
          <tr>
            <th>Slot</th>
            <th>Item</th>
            <th>Enchant</th>
            <th>Source</th>
            <th>Options</th>
          </tr>
        </thead>
        <tbody>
          {BUILD_SLOTS.map((slot) => {
            const row = bySlot.get(slot);
            return (
              <tr key={slot} data-testid="build-gear-row" data-slot={slot}>
                <th scope="row">{BUILD_SLOT_LABELS[slot]}</th>
                {row ? (
                  <>
                    <td>
                      {row.blockedBy ? (
                        <span className="muted">Empty: {row.blockedBy} uses both hands</span>
                      ) : (
                        <>
                          {row.item.iconUrl && <img className="icon" src={row.item.iconUrl} alt="" />}
                          <ArtifactLink type="item" slug={row.item.slug} name={row.item.name} quality={row.item.quality} />{' '}
                          <Badge status={row.item.dataStatus} build={row.item.sourceBuild} />
                          {row.upgrade && (
                            <span className="badge upgrade" data-testid="build-upgrade">
                              Upgrade
                            </span>
                          )}
                          {row.note && <div className="muted">{row.note}</div>}
                        </>
                      )}
                    </td>
                    <td>
                      {row.enchant ? (
                        row.enchant.recipe ? (
                          <ArtifactLink type="recipe" slug={row.enchant.recipe.slug} name={row.enchant.recipe.name}>
                            {row.enchant.text || row.enchant.recipe.name}
                          </ArtifactLink>
                        ) : (
                          row.enchant.text || `Enchant ${row.enchant.id}`
                        )
                      ) : (
                        <span className="muted">none</span>
                      )}
                    </td>
                    <td>{row.blockedBy ? <span className="muted">&mdash;</span> : <Sources sources={row.item.sources} unknown={row.item.sourceUnknown} />}</td>
                    <td>
                      <OptionsCell row={row} gear={gear} editable={editable} onSelect={onSelect} />
                      {error?.slot === slot && (
                        <div className="error" role="alert">
                          {error.message}
                        </div>
                      )}
                    </td>
                  </>
                ) : (
                  <td colSpan={4} className="muted">
                    nothing suitable found
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
